import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { UserProfileApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import {
  type FamilyContactEditFormSchemaType,
  ProfileSettingsFormType,
} from "@/schemas";
import { createAuthenticatedApi } from "@/lib/utils";

interface IFamilyContactsUpdateContactData
  extends Omit<FamilyContactEditFormSchemaType, "address"> {
  userContactId: number;
  addressLine1?: string | null;
}

export const useFamilyContactsUpdate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async ({
      id,
      updateUserContactData,
    }: {
      id: number;
      updateUserContactData: IFamilyContactsUpdateContactData;
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(UserProfileApi, accessToken);
      
      // Map form data to API request, excluding homePhone which is not in API
      const { homePhone, ...apiData } = updateUserContactData;
      
      return service.apiUserProfileIdContactsPut({
        id: id,
        updateUserContactRequest: [{
          userContactId: apiData.userContactId,
          firstName: apiData.firstName,
          lastName: apiData.lastName,
          email: apiData.email,
          cellPhone: apiData.cellPhone,
          addressLine1: apiData.addressLine1 || null,
          sendRoboDials: apiData.sendRoboDials ?? false,
          sendRoboTexts: apiData.sendRoboTexts ?? false,
          sendEmail: apiData.sendEmail ?? false,
          sendStatements: apiData.sendStatements ?? false,
        }],
      });
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.FamilyContactsGet, variables.id, "all"],
      });
      toast({
        title: "Contact updated",
        description: "Contact updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any, variables) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update contact";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
